// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import useBeerDuplicateReviews from '../../hooks/useBeerDuplicateReviews';
import BeerDuplicateReviewDialog from './BeerDuplicateReviewDialog';
import {
  buildBeerDuplicateReviewPath,
  queueReviewIdsFromRows,
} from '../../utils/beer/beerDuplicateReviewQueue';

function FilterBar({ statusFilter, onStatusChange }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="status-filter-label">Status</InputLabel>
        <Select
          labelId="status-filter-label"
          id="status-filter"
          value={statusFilter}
          label="Status"
          onChange={onStatusChange}
        >
          <MenuItem value="pending">pending</MenuItem>
          <MenuItem value="reviewed">reviewed</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
}

FilterBar.propTypes = {
  statusFilter: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

function formatText(value) {
  return value != null && String(value).trim() ? String(value) : '—';
}

function DupesTableRow({
  row,
  showBreweryColumn,
  onReview,
  canReview,
}) {
  const matched = row.matchedBeer || {};
  const newBeer = row.newBeer || {};
  const handleReviewClick = useCallback(() => {
    onReview(row);
  }, [onReview, row]);

  const breweryCell = showBreweryColumn ? (
    <TableCell>
      {row.breweryId ? (
        <Link
          component={RouterLink}
          to={`/brewery/${row.breweryId}`}
          underline="hover"
        >
          {formatText(row.breweryName)}
        </Link>
      ) : (
        formatText(row.breweryName)
      )}
    </TableCell>
  ) : null;

  return (
    <TableRow hover>
      {breweryCell}
      <TableCell>{formatText(matched.name)}</TableCell>
      <TableCell>{formatText(newBeer.name)}</TableCell>
      <TableCell>
        {canReview ? (
          <Button size="small" variant="outlined" onClick={handleReviewClick}>
            Review
          </Button>
        ) : (
          '—'
        )}
      </TableCell>
    </TableRow>
  );
}

DupesTableRow.propTypes = {
  row: PropTypes.object.isRequired,
  showBreweryColumn: PropTypes.bool.isRequired,
  onReview: PropTypes.func.isRequired,
  canReview: PropTypes.bool.isRequired,
};

function DupesTableHead({ showBreweryColumn }) {
  return (
    <TableHead>
      <TableRow>
        {showBreweryColumn ? (
          <TableCell sx={{ fontWeight: 600 }}>Brewery</TableCell>
        ) : null}
        <TableCell sx={{ fontWeight: 600 }}>Existing beer</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Incoming beer</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Review</TableCell>
      </TableRow>
    </TableHead>
  );
}

DupesTableHead.propTypes = {
  showBreweryColumn: PropTypes.bool.isRequired,
};

/**
 * BeerDupesSection
 * Displays beer duplicate review records, optionally scoped to one brewery or global.
 */
function BeerDupesSection({ breweryId, globalScope = false }) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogReviewId, setDialogReviewId] = useState(null);
  const { reviews, loading, error, refresh } = useBeerDuplicateReviews(
    breweryId,
    statusFilter,
    { globalScope },
  );

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
  }, []);

  const handleReview = useCallback((row) => {
    if (statusFilter !== 'pending') {
      return;
    }
    if (globalScope) {
      navigate(buildBeerDuplicateReviewPath(row.id), {
        state: { queueReviewIds: queueReviewIdsFromRows(reviews) },
      });
      return;
    }
    setDialogReviewId(row.id);
    setDialogOpen(true);
  }, [globalScope, navigate, reviews, statusFilter]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setDialogReviewId(null);
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  const emptyMessage = (
    <Typography variant="body2" sx={{ p: 2 }}>
      No beer duplicate reviews match the current filter.
    </Typography>
  );

  const tableContent = reviews.length === 0 ? (
    emptyMessage
  ) : (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader aria-label="Beer duplicate reviews">
        <DupesTableHead showBreweryColumn={globalScope} />
        <TableBody>
          {reviews.map((row) => (
            <DupesTableRow
              key={row.id}
              row={row}
              showBreweryColumn={globalScope}
              onReview={handleReview}
              canReview={statusFilter === 'pending'}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <FilterBar
        statusFilter={statusFilter}
        onStatusChange={handleStatusFilterChange}
      />
      {tableContent}
      {!globalScope && breweryId ? (
        <BeerDuplicateReviewDialog
          open={dialogOpen}
          reviewId={dialogReviewId}
          breweryId={breweryId}
          onClose={handleDialogClose}
        />
      ) : null}
    </Box>
  );
}

BeerDupesSection.propTypes = {
  breweryId: PropTypes.string,
  globalScope: PropTypes.bool,
};

BeerDupesSection.defaultProps = {
  breweryId: null,
  globalScope: false,
};

export default BeerDupesSection;
